import React, { forwardRef } from "react";
import QRCode from "react-qr-code";
import { qrBaseUrl } from "../../server";

const PrintableTC = forwardRef(({ tc: incomingTc, tcData }, ref) => {
  const tc = incomingTc || tcData;
  if (!tc) return null;

  const formatDate = (dateVal) => {
    if (!dateVal) return "N/A";
    let str = String(dateVal).trim();
    if (!str || str.startsWith("0001-") || str.startsWith("1900-") || str === "N/A") return "N/A";

    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      const parts = str.split("T")[0].split("-");
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        if (year !== "0001" && year !== "1900") {
          return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
        }
      }
    }

    const d = new Date(str);
    if (isNaN(d.getTime())) return "N/A";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    if (year <= 1900) return "N/A";
    return `${day}/${month}/${year}`;
  };

  const dobVal =
    tc.student?.dateOfBirth ||
    tc.student?.DateOfBirth ||
    tc.student?.dob ||
    tc.student?.DOB;

  const admissionDateVal =
    tc.student?.dateOfAdmission ||
    tc.student?.DateOfAdmission ||
    tc.student?.admissionDate ||
    tc.student?.AdmissionDate ||
    tc.student?.createdAt ||
    tc.student?.CreatedAt;

  const studentObj = tc.student || tc.Student || tc;
  const studentName = (studentObj.studentName || studentObj.StudentName || tc.studentName || "N/A").toUpperCase();
  const admissionNo = studentObj.admissionNumber || studentObj.AdmissionNumber || tc.admissionNumber || "N/A";
  const motherName = (studentObj.motherName || studentObj.MotherName || tc.motherName || "N/A").toUpperCase();
  const fatherName = (studentObj.fatherName || studentObj.FatherName || studentObj.localGuardianName || tc.fatherName || "N/A").toUpperCase();
  const className = studentObj.class || studentObj.Class || tc.class || "";
  const sectionName = studentObj.section || studentObj.Section || tc.section || "";

  const qrVerificationUrl = `${qrBaseUrl}/verify/transferCertificate/${studentObj.uniqueId || tc.tcNumber || tc.id}`;

  return (
    <div
      ref={ref}
      className="w-[800px] bg-white p-12 text-gray-800 shadow-xl print:shadow-none border border-gray-200 print:border-none relative z-0 mt-4 mx-auto"
      style={{ fontFamily: "Arial, sans-serif" }}
    >
      {/* Background Watermark/Logo Option */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.07] flex items-center justify-center z-[-1]">
        <img
          src="/assets/images/logo.jpg"
          alt="Watermark"
          className="w-full h-fit object-cover"
        />
      </div>

      {/* Header with Logo */}
      <div className="flex items-center justify-center mb-8 pb-6 border-b-4 border-gray-800 relative z-10">
        <div className="text-center">
          <img
            src="/assets/images/logo.jpg"
            alt="School Logo"
            className="w-40 object-contain mx-auto -mt-16"
          />
          <div className="text-3xl font-black uppercase tracking-widest mb-1 mt-4">
            DEE NOVO SCHOOL
          </div>
          <div className="text-sm font-semibold uppercase tracking-widest text-gray-600">
            Affiliated to CBSE • Dumka, Jharkhand
          </div>
          <div className="text-xl font-black uppercase tracking-[0.2em] mt-6 border-4 border-gray-800 inline-block px-8 py-2 rounded-full">
            TRANSFER CERTIFICATE
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-8 text-sm font-bold uppercase relative z-10">
        <div>
          Admin/SR No:{" "}
          <span className="border-b border-dashed border-gray-400 px-4 min-w-[150px] inline-block mb-1">
            {admissionNo}
          </span>
        </div>

        {/* QR Verification Code */}
        <div className="flex items-center gap-2 border border-gray-300 p-1.5 rounded-lg bg-gray-50">
          <QRCode value={qrVerificationUrl} size={42} />
          <div className="text-left">
            <div className="text-[9px] font-black text-gray-900 uppercase tracking-tight">
              QR Verification
            </div>
            <div className="text-[8px] text-gray-500 font-medium leading-none">
              Scan to verify
            </div>
          </div>
        </div>

        <div>
          TC No:{" "}
          <span className="border-b border-dashed border-gray-400 px-4 min-w-[100px] inline-block text-red-700 mb-1">
            {tc.tcNumber}
          </span>
        </div>
      </div>

      <table className="w-full text-left space-y-6 text-base leading-loose border-collapse mt-4 relative z-10">
        <tbody>
          <tr>
            <td className="w-1/3 py-2 font-bold uppercase pr-4">
              1. Name of Pupil
            </td>
            <td className="w-2/3 py-2 border-b-2 border-dashed border-gray-400 font-black text-lg">
              : {studentName}
            </td>
          </tr>
          <tr>
            <td className="py-2 font-bold uppercase pr-4">2. Mother's Name</td>
            <td className="py-2 border-b border-dashed border-gray-400 font-bold text-gray-800">
              : {motherName}
            </td>
          </tr>
          <tr>
            <td className="py-2 font-bold uppercase pr-4">3. Father's Name</td>
            <td className="py-2 border-b border-dashed border-gray-400 font-bold text-gray-800">
              : {fatherName}
            </td>
          </tr>
          <tr>
            <td className="py-2 font-bold uppercase pr-4">4. Date of Birth</td>
            <td className="py-2 border-b border-dashed border-gray-400 font-bold text-gray-800">
              : {formatDate(dobVal)}
            </td>
          </tr>
          <tr>
            <td className="py-2 font-bold uppercase pr-4">
              5. Class & Section
            </td>
            <td className="py-2 border-b border-dashed border-gray-400 font-bold text-gray-800">
              : VOL. {className} {sectionName}
            </td>
          </tr>
          <tr>
            <td className="py-2 font-bold uppercase pr-4">
              6. Date of Admission
            </td>
            <td className="py-2 border-b border-dashed border-gray-400 font-bold text-gray-800">
              : {formatDate(admissionDateVal)}
            </td>
          </tr>
          <tr>
            <td className="py-2 font-bold uppercase pr-4">
              7. Application Date for TC
            </td>
            <td className="py-2 border-b border-dashed border-gray-400 font-bold text-gray-800">
              : {formatDate(tc.applicationDate || tc.ApplicationDate)}
            </td>
          </tr>
          <tr>
            <td className="py-2 font-bold uppercase pr-4">
              8. Issue Date of TC
            </td>
            <td className="py-2 border-b border-dashed border-gray-400 font-bold text-gray-800">
              : {formatDate(tc.issueDate || tc.IssueDate)}
            </td>
          </tr>
          <tr>
            <td className="py-2 font-bold uppercase pr-4">
              9. Reason for leaving the school
            </td>
            <td className="py-2 border-b border-dashed border-gray-400 font-bold text-gray-800">
              : {tc.reasonForLeaving?.toUpperCase() || "N/A"}
            </td>
          </tr>
          <tr>
            <td className="py-2 font-bold uppercase pr-4">
              10. General Conduct
            </td>
            <td className="py-2 border-b border-dashed border-gray-400 font-bold text-gray-800">
              : {tc.conduct?.toUpperCase()}
            </td>
          </tr>
          <tr>
            <td className="py-2 font-bold uppercase pr-4">
              11. Any other remarks
            </td>
            <td className="py-2 border-b border-dashed border-gray-400 font-bold text-gray-800">
              : {tc.remarks?.toUpperCase() || "NIL"}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="flex justify-between items-end mt-32 mx-4 font-bold text-sm uppercase relative z-10">
        <div className="text-center">
          <div className="w-48 border-t-2 border-gray-800 pt-2">
            Class Teacher
          </div>
        </div>
        <div className="text-center">
          <div className="w-48 border-t-2 border-gray-800 pt-2">Checked By</div>
        </div>
        <div className="text-center">
          <div className="w-48 border-t-2 border-gray-800 pt-2">Principal</div>
        </div>
      </div>
    </div>
  );
});

export default PrintableTC;
